/**
 * CK Share — Decentralized Nostr Public Relay Transport (Layer 2)
 * Zero-Server, 100% Free Ephemeral Mesh using public Nostr WebSocket Relays.
 */

export interface NostrEvent {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number; // Ephemeral range: 20000 - 29999
  tags: string[][];
  content: string; // E2EE encrypted ciphertext
  sig: string;
}

export type NostrEventCallback = (event: NostrEvent) => void;

const DEFAULT_RELAYS = [
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.primal.net'
];

export class NostrRelayPool {
  private sockets: Map<string, WebSocket> = new Map();
  private relays: string[];
  private subscriptionId: string = '';
  private onEventCallback: NostrEventCallback | null = null;
  private seenEventIds: Set<string> = new Set();
  private roomId: string = '';
  private isConnected = false;

  constructor(relays: string[] = DEFAULT_RELAYS) {
    this.relays = relays;
  }

  public connect(roomId: string, onEvent: NostrEventCallback) {
    this.roomId = roomId.toUpperCase();
    this.onEventCallback = onEvent;
    this.subscriptionId = 'sub_' + Math.random().toString(36).substring(2, 8);
    this.isConnected = true;

    for (const url of this.relays) {
      this.initRelay(url);
    }
  }

  private initRelay(url: string) {
    try {
      const ws = new WebSocket(url);
      this.sockets.set(url, ws);

      ws.onopen = () => {
        // Subscribe to ephemeral kind 20077 tagged with room ID
        const filter = {
          kinds: [20077],
          '#d': [this.roomId],
          since: Math.floor(Date.now() / 1000) - 60
        };
        const reqMsg = JSON.stringify(['REQ', this.subscriptionId, filter]);
        ws.send(reqMsg);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (Array.isArray(data) && data[0] === 'EVENT' && data[1] === this.subscriptionId) {
            const event: NostrEvent = data[2];
            if (event && event.id && !this.seenEventIds.has(event.id)) {
              this.seenEventIds.add(event.id);
              // Clean up seen set if too large
              if (this.seenEventIds.size > 500) {
                const arr = Array.from(this.seenEventIds);
                this.seenEventIds = new Set(arr.slice(-250));
              }
              if (this.onEventCallback) {
                this.onEventCallback(event);
              }
            }
          }
        } catch {
          // ignore unparseable relay message
        }
      };

      ws.onerror = () => {};
      ws.onclose = () => {
        this.sockets.delete(url);
        if (this.isConnected) {
          // Reconnect after 5s
          setTimeout(() => {
            if (this.isConnected) this.initRelay(url);
          }, 5000);
        }
      };
    } catch {
      // Relay socket failed to initialize
    }
  }

  public async publish(payloadJson: string): Promise<void> {
    const createdAt = Math.floor(Date.now() / 1000);
    const pubkey = 'ck_' + this.roomId.toLowerCase();
    const eventData = [
      0,
      pubkey,
      createdAt,
      20077,
      [['d', this.roomId], ['t', 'ckshare']],
      payloadJson
    ];
    
    // Hash event data to generate ID
    const rawStr = JSON.stringify(eventData);
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawStr));
    const id = Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');

    const event: NostrEvent = {
      id,
      pubkey,
      created_at: createdAt,
      kind: 20077,
      tags: [['d', this.roomId], ['t', 'ckshare']],
      content: payloadJson,
      sig: 'ck_ephemeral_sig'
    };

    this.seenEventIds.add(id);

    const eventMsg = JSON.stringify(['EVENT', event]);
    for (const ws of this.sockets.values()) {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(eventMsg);
        } catch {
          // ignore individual send failure
        }
      }
    }
  }

  public disconnect() {
    this.isConnected = false;
    for (const ws of this.sockets.values()) {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(['CLOSE', this.subscriptionId]));
        }
        ws.close();
      } catch {}
    }
    this.sockets.clear();
  }
}
